import React, { useMemo } from "react";
import { ErrorMessage, Textfield } from "widgets";
import {
  DENOMINATION_UNITS,
  CASH_IN_HAND_MISMATCH_THRESHOLD,
} from "constants/constants";
import { CurrencyCode } from "constants/enums";
import { formatCurrency } from "utils/formatCurrency";
import styles from "./CashDenominationForm.module.scss";

const HeaderRow = ({ label, amount }) => (
  <div className={styles.headerRow}>
    <label>{label}:</label>
    <span className={styles.prefilled}>
      {formatCurrency(amount, { currencyCode: CurrencyCode.INR })}
    </span>
  </div>
);

const CashDenominationForm = ({
  values,
  amountDetails = [],
  amountToValidate = 0,
  amountLabel,
  handleChangeCashDenominations,
}) => {
  const denominationDetails = values?.denominationDetails ?? {};

  const totalDenominationValue = useMemo(() => {
    return DENOMINATION_UNITS.reduce((total, { value }) => {
      const count = Number(denominationDetails[`count_${value}`]);
      return total + (isNaN(count) ? 0 : count * value);
    }, 0);
  }, [denominationDetails]);

  const isMismatch =
    Math.abs(totalDenominationValue - Number(amountToValidate)) >=
    CASH_IN_HAND_MISMATCH_THRESHOLD;

  if (!Array.isArray(amountDetails) || amountDetails.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {amountDetails.map(({ label, amount }, index) => (
        <HeaderRow key={`${label}-${index}`} label={label} amount={amount} />
      ))}

      <div className={styles.grid}>
        {DENOMINATION_UNITS.map(({ value, label }) => {
          const count = Number(denominationDetails[`count_${value}`]) || 0;
          const calculatedAmount = count * value;

          return (
            <div key={value} className={styles.row}>
              <label className={styles.label}>{label}</label>

              <Textfield
                value={count}
                onChange={(e) =>
                  handleChangeCashDenominations(e.target.value, value)
                }
                className={styles.countInput}
              />

              <span className={styles.equal}>=</span>

              <Textfield
                value={formatCurrency(calculatedAmount, {
                  currencyCode: CurrencyCode.INR,
                })}
                disabled
                className={styles.amount}
              />
            </div>
          );
        })}
      </div>

      <div className={styles.footer}>
        <label>{amountLabel}</label>
        <Textfield
          value={formatCurrency(totalDenominationValue, {
            currencyCode: CurrencyCode.INR,
          })}
          disabled
          className={styles.total}
        />
      </div>

      {isMismatch && (
        <ErrorMessage className={styles.error}>
          Denomination total (
          {formatCurrency(totalDenominationValue, {
            currencyCode: CurrencyCode.INR,
          })}
          ) does not match the expected amount (
          {formatCurrency(amountToValidate, {
            currencyCode: CurrencyCode.INR,
          })}
          ). Please adjust the counts.
        </ErrorMessage>
      )}
    </div>
  );
};

export default React.memo(CashDenominationForm);
